import DOMPurify from 'dompurify';
import React, { useState } from 'react';
import { Button, ModalDialog, ModalOverlay } from '@flos/react-ui';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useUpdateCoveragePackageInformation } from '../../../../recoil/hooks/useUpdateCoveragePackageInformation';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { useAddOrRemoveCoverage } from '../../../../recoil/hooks/useAddOrRemoveCoverage';
import { useCalculateCoveragesPrice } from '../../../../recoil/hooks/useCalculateCoveragesPrice';
import { useRecoilValue } from 'recoil';
import { deductibleOptionsSelector } from '../../../../recoil/selectors/deductibleOptionsSelector';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import {
    getCoverageFieldToUpdate,
    type AdditionalCoverageProps
} from '../../config/AdditionalCoveragesConfig';
import { getOnsaCoverable } from '../../../../utils/coverablesUtil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';

const AdditionalCoverageInputModal = ({
    coverage,
    isLoading,
    setIsLoading,
    showModal,
    setShowModal,
    closeModal
}: {
    coverage: AdditionalCoverageProps;
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    closeModal: () => void;
}): React.JSX.Element => {
    const Input = coverage.input;
    const getHippoText = useHippoText();
    const deductibleOptions = useRecoilValue(deductibleOptionsSelector(coverage.coverageId));
    const [selectedDeductible, setSelectedDeductible] = useState(deductibleOptions[0].optionID);
    const { addOrRemoveCoverage } = useAddOrRemoveCoverage();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const { calculateCoveragesPrice } = useCalculateCoveragesPrice();
    const { updateCoveragePackageInformation } = useUpdateCoveragePackageInformation();
    const { flavourName, fixedId } = useRecoilValue(getOnsaCurrentProductSelector);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const coverable = getOnsaCoverable(coverables, flavourName, fixedId);
    const fieldToUpdate = getCoverageFieldToUpdate(coverage.coverageId);

    const handleAddOrEditCoverage = (): void => {
        const updateCovInfo = async (): Promise<void> => {
            if (!coverable.coverages?.find((cov) => cov.coverageId === coverage.coverageId)) {
                await addOrRemoveCoverage(
                    coverage.coverageId,
                    coverable.packageGroupName ?? 'Default'
                );
            }
            await updateCoveragePackageInformation({
                [fieldToUpdate]: selectedDeductible
            });
        };
        setIsLoading(true);
        void updateCovInfo()
            .then(calculateCoveragesPrice)
            .then((response) => {
                updateCoverableInformation({
                    coverages: response.coverages,
                    monthlyPrice: response.coverablePrice.monthlyPrice,
                    yearlyPrice: response.coverablePrice.yearlyPrice,
                    [fieldToUpdate]: selectedDeductible
                });
                setIsLoading(false);
                setShowModal(false);
            });
    };

    return (
        <ModalOverlay isOpen={showModal} onDismiss={closeModal}>
            <ModalDialog closeIconHandler={closeModal}>
                <div className="u-spacing-inset-l">
                    <div className="u-spacing-inset-none">
                        <h4 className="u-spacing-stack-sm">
                            {DOMPurify.sanitize(getHippoText(coverage?.coverageName ?? ''))}
                        </h4>
                        <p
                            className="u-spacing-stack-sm"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    getHippoText(coverage?.inputModalProps?.modalSubheader ?? '')
                                )
                            }}
                        />
                    </div>
                    {Input && (
                        <Input
                            isLoading={isLoading}
                            coverageId={coverage.coverageId}
                            setSelectedDeductible={setSelectedDeductible}
                        />
                    )}
                    <Button onClick={handleAddOrEditCoverage} disabled={isLoading}>
                        {getHippoText('DSS-COMMON-ADD-COVERAGE')}
                    </Button>
                </div>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default AdditionalCoverageInputModal;
